import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  BaseSyntheticEvent,
} from 'react';
import { AppMain } from '../layout/appMain';
import { Activity } from '../models/activity';
import { SuccessfulDialog } from '../models/dialog';
import {
  formatActivityDate,
  getDateByWeekNumber,
  getEndDateAccording,
  getStartDateAccording,
} from '../services/dateService';
import { AppConfirmationDelete } from '../components/app/confirmation/appConfiramationDelete';
import { AppConfirmationSuccessful } from '../components/app/confirmation/appConfirmationSuccessful';
import {
  Box,
  Checkbox,
  Dialog,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AppDialogTitle } from '../layout/appDialogTitle';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  PREFERENCES,
  ProfilePreferencesField,
  ViewMode,
} from '../models/preferences';
import { useSelector } from 'react-redux';
import { selectPreferences } from '../store/preferencesSlice';
import {
  selectProfileId,
  selectProfileWorkingHours,
  selectUserLocation,
} from '../store/profileSlice';
import AppLoading from '../components/app/appLoading';
import useAppSnackbar from '../hooks/useAppSnackbar';
import { useActivities } from '../hooks/useActivities';
import { AxiosError } from 'axios';
import { endOfDay, endOfMonth, formatISO, isSameMonth } from 'date-fns';
import {
  useAddActivityMutation,
  useDeleteActivityMutation,
  useEditActivityMutation,
} from '../store/activities/activitiesService';
import { AppActivityViews } from '../components/app/appActivityViews';
import { useSearchParams } from 'react-router-dom';
import { ActivityToolbox } from '../components/activity/activityToolbox';
import { ActivityDialogExport } from '../components/activity/activityDialogExport';
import {
  ActivityForm,
  ActivityFormData,
} from '../components/activity/activityForm';
import { AppActivityTableViews } from '../components/app/appActivityTableViews';
import { viewModeOptions } from '../constants';
import { convertHoursToMinutes } from '../services/conversionService';
import { generateActivityFormData } from '../services/activityService';
import {
   formatDateOnly,
} from '../services/dateService';

const styles = {
  viewModeMobile: { minWidth: '200px', margin: '12px' },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'end',
    gap: 0.5,
    mt: { xs: 0, sm: -5 },
  },
};

export function PageActivity(): JSX.Element {
  const preference = useSelector(selectPreferences);
  const employeeId = useSelector(selectProfileId);
  const userLocation = useSelector(selectUserLocation);
  const userWorkingHours = useSelector(selectProfileWorkingHours);

  const {
    [ProfilePreferencesField.PROJECT]: preferredProject,
    [ProfilePreferencesField.TASK]: preferredTask,
  } = useSelector(selectPreferences);

  const [searchParams, setSearchParams] = useSearchParams();
  const queryParams = useMemo(
    () => Object.fromEntries(new URLSearchParams(searchParams)),
    [searchParams]
  );
  const [dateActivity, setDateActivity] = useState<Date>(new Date());
  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false);
  const [successfulDialog, setSuccessfulDialog] = useState<SuccessfulDialog>({
    isOpen: false,
    message: null,
  });
  const [selectedActivity, setSelectedActivity] = useState<ActivityFormData>(
    generateActivityFormData()
  );
  const [previousActivity, setPreviousActivity] =
    useState<ActivityFormData | null>(null);
  const [isOpenAddActivity, setIsOpenAddActivity] = useState<boolean>(false);
  const [isOpenEditActivity, setIsOpenEditActivity] = useState<boolean>(false);
  const [isOpenExport, setIsOpenExport] = useState<boolean>(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [showFullMonth, setShowFullMonth] = useState<boolean>(false);

  const viewMode = useMemo(
    () =>
      (queryParams.viewMode as ViewMode) ||
      PREFERENCES[preference?.activityView ?? 0],
    [preference?.activityView, queryParams.viewMode]
  );
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { onErrorSnackbar } = useAppSnackbar();

  const isDateActivityInCurrentMonth = useMemo(
    () => isSameMonth(new Date(), dateActivity),
    [dateActivity]
  );

  const startDate = useMemo(
    () => formatISO(getStartDateAccording(viewMode, dateActivity)),
    [dateActivity, viewMode]
  );

  const endDate = useMemo(() => {
    if (viewMode === ViewMode.OLD_VIEW && isDateActivityInCurrentMonth) {
      const date = showFullMonth
        ? endOfMonth(dateActivity)
        : endOfDay(new Date());

      return formatISO(date);
    }

    return formatISO(getEndDateAccording(viewMode, dateActivity));
  }, [dateActivity, showFullMonth, isDateActivityInCurrentMonth, viewMode]);

  const { activities, activitiesError, isFetching, isLoading } = useActivities({
    internalId: employeeId || '',
    startDate: formatActivityDate(new Date(formatDateOnly(startDate))),
    endDate: formatActivityDate(new Date(formatDateOnly(endDate))),
  });

  const [addActivity] = useAddActivityMutation();
  const [editActivity] = useEditActivityMutation();
  const [deleteActivity] = useDeleteActivityMutation();

  const isLoadingActivities = useMemo(
    () => isLoading || isFetching,
    [isFetching, isLoading]
  );

  const isLoadingActivityCards = useMemo(
    () => isLoadingActivities && viewMode !== ViewMode.MONTH,
    [isLoadingActivities, viewMode]
  );

  useEffect(() => {
    if (activitiesError) {
      onErrorSnackbar(
        activitiesError as AxiosError,
        "Data couldn't be retrieved."
      );
    }
  }, [activitiesError, onErrorSnackbar]);

  const handleChangeViewMode = useCallback(
    (arg: ViewMode) => {
      setSearchParams({ viewMode: arg });
    },
    [setSearchParams]
  );

  const handleChangeMobileViewMode = useCallback(
    (event) => {
      setSearchParams({ viewMode: event.target.value });
    },
    [setSearchParams]
  );

  const onDayActivities = useCallback(
    (activityDate: string) => {
      setDateActivity(new Date(activityDate));
      setSearchParams({ viewMode: ViewMode.DAY });
    },
    [setSearchParams]
  );

  const onWeekActivities = useCallback(
    (weekNumber: number) => {
      setDateActivity(getDateByWeekNumber(weekNumber, dateActivity));
      setSearchParams({ viewMode: ViewMode.WEEK });
    },
    [dateActivity, setSearchParams]
  );

  /*  DELETE ACTIVITY */
  const onDeleteActivity = useCallback((activity: Activity) => {
    setSelectedActivity(generateActivityFormData(activity));
    setIsOpenConfirmation(true);
  }, []);

  const onConfirmDeleteActivity = useCallback(() => {
    if (!selectedActivity) {
      return;
    }
    deleteActivity(selectedActivity.id)
      .unwrap()
      .then(() => {
        setIsOpenConfirmation(false);
        setSuccessfulDialog({
          isOpen: true,
          message: 'Successfully deleted',
        });
      })
      .then(() => {
        setTimeout(() => {
          setSuccessfulDialog({
            isOpen: false,
            message: null,
          });

          if (
            previousActivity &&
            previousActivity.project?.id === selectedActivity.project?.id &&
            previousActivity.task?.id === selectedActivity.task?.id &&
            previousActivity.hours === selectedActivity.hours &&
            previousActivity.comment === selectedActivity.comment
          ) {
            setPreviousActivity(null);
          }
          setSelectedActivity(generateActivityFormData());
        }, 2000);
      })
      .catch((error) => {
        setIsOpenConfirmation(false);
        onErrorSnackbar(error, 'Could not delete activity');
      });
  }, [deleteActivity, onErrorSnackbar, previousActivity, selectedActivity]);

  const onCloseDeleteDialog = useCallback(() => {
    setIsOpenConfirmation(false);
    setSelectedActivity(generateActivityFormData());
  }, []);

  const onCloseConfirmDialog = useCallback(() => {
    setSuccessfulDialog({
      isOpen: false,
      message: null,
    });
    setSelectedActivity(generateActivityFormData());
  }, []);

  /*  ADD ACTIVITY */
  const onOpenAddActivity = useCallback(
    (activityDate?) => {
      setSelectedActivity({
        id: previousActivity?.id ? previousActivity.id : '',
        project: previousActivity?.project
          ? previousActivity.project
          : preferredProject
          ? preferredProject
          : null,
        task: previousActivity?.task
          ? previousActivity.task
          : preferredTask
          ? preferredTask
          : null,
        date: formatISO(activityDate || dateActivity),
        hours: previousActivity?.hours
          ? previousActivity.hours
          : preferredProject && preferredTask
          ? userWorkingHours.toString()
          : '',
        comment: previousActivity?.comment ? previousActivity.comment : '',
        ticketIdentification: previousActivity?.ticketIdentification
          ? previousActivity.ticketIdentification
          : '',
        isAMS: previousActivity?.isAMS || preferredProject?.isAMS || false,
      });

      setIsOpenAddActivity(true);
    },
    [
      dateActivity,
      preferredProject,
      preferredTask,
      previousActivity,
      userWorkingHours,
    ]
  );

  const onCloseAddActivity = useCallback(() => {
    setIsOpenAddActivity(false);
  }, []);

  const onSubmitAddActivity = useCallback(
    (data: ActivityFormData<Date>) => {
      setIsLoadingSubmit(true);
      const payload = {
        employeeId: employeeId,
        comment: data.comment,
        date: formatActivityDate(data.date),
        minutes: convertHoursToMinutes(Number(data.hours)),
        taskId: data.task?.id,
        ticketIdentification: (data.isAMS || data.task?.isAMS) ? data.ticketIdentification : null,
      };

      console.log('Payload for adding activity:', payload);
      console.log('Data received from form:', data);

      addActivity(payload)
        .unwrap()
        .then(() => {
          onCloseAddActivity();
          setPreviousActivity({
            id: data.id,
            project: data.project,
            task: data.task,
            hours: data.hours,
            comment: data.comment,
            date: formatISO(data.date),
            ticketIdentification: (data.isAMS || data.task?.isAMS) ? data.ticketIdentification : null,
            isAMS: data.isAMS,
          });
        })
        .catch((error) => {
          onCloseAddActivity();
          onErrorSnackbar(error, 'Could not add activity');
        })
        .finally(() => setIsLoadingSubmit(false));
    },
    [addActivity, employeeId, onCloseAddActivity, onErrorSnackbar]
  );

  /*  EDIT ACTIVITY */
  const onCloseEditDialogActivity = useCallback(() => {
    setIsOpenEditActivity(false);
    setSelectedActivity(generateActivityFormData());
  }, []);

  const onSubmitEditActivity = useCallback(
    (data: ActivityFormData<Date>) => {
      setIsLoadingSubmit(true);
      if (selectedActivity) {
        const payload = {
          comment: data.comment,
          date: formatActivityDate(data.date),
          minutes: convertHoursToMinutes(Number(data.hours)),
          taskId: data.task?.id,
          employeeId: employeeId,
          ticketIdentification: data.ticketIdentification,
        };

        editActivity({
          idActivity: selectedActivity.id,
          editActivityInfo: payload,
        })
          .unwrap()
          .then(() => {
            setIsOpenEditActivity(false);
            setSelectedActivity(generateActivityFormData());
          })
          .catch((error) => {
            setIsOpenEditActivity(false);
            onErrorSnackbar(error, 'Could not edit activity');
          })
          .finally(() => setIsLoadingSubmit(false));

        setIsLoadingSubmit(false);
      }
    },
    [selectedActivity, employeeId, editActivity, onErrorSnackbar]
  );

  const onEditActivity = useCallback((activity: Activity) => {
    setSelectedActivity(generateActivityFormData(activity));
    setIsOpenEditActivity(true);
  }, []);

  const onAddActivity = useCallback(
    (addDate: string) => {
      const formattedDate = new Date(formatDateOnly(addDate));
      setDateActivity(formattedDate);
      onOpenAddActivity(formattedDate);
    },
    [onOpenAddActivity]
  );

  const messageConfirmationDelete = useMemo(
    () =>
      `Delete activity ${selectedActivity?.project?.label} - ${selectedActivity?.task?.name} (${selectedActivity?.hours}h) ?`,
    [selectedActivity]
  );

  const openExportBox = useCallback(() => {
    setIsOpenExport(true);
  }, []);

  const isAddActivityButtonVisible = useMemo(
    () => viewMode === ViewMode.DAY,
    [viewMode]
  );

  const headerButtons = useMemo(() => {
    return [
      { icon: FileDownloadOutlinedIcon, onClick: openExportBox },
      ...(isAddActivityButtonVisible
        ? [{ icon: AddIcon, onClick: () => onOpenAddActivity() }]
        : []),
    ];
  }, [openExportBox, isAddActivityButtonVisible, onOpenAddActivity]);

  const userDataLocation = useMemo(() => {
    return userLocation || '';
  }, [userLocation]);

  const handleChangeOldViewShowDates = useCallback(
    (event: BaseSyntheticEvent) => {
      setShowFullMonth(event.target.checked);
    },
    []
  );

  return (
    <AppMain buttons={headerButtons}>
      <ActivityToolbox
        handleChangeViewMode={handleChangeViewMode}
        setDateActivity={setDateActivity}
        onOpenAddActivity={onOpenAddActivity}
        viewMode={viewMode}
        dateActivity={dateActivity}
        activities={activities}
        isAddActivityButtonVisible={isAddActivityButtonVisible}
        setIsOpenExport={setIsOpenExport}
        employeeWorkingHours={userWorkingHours}
      />

      {isMobile && (
        <TextField
          select
          fullWidth
          label="View mode"
          sx={styles.viewModeMobile}
          value={viewMode}
          onChange={handleChangeMobileViewMode}
        >
          {viewModeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}

      {isLoadingActivityCards ? (
        <AppLoading />
      ) : (
        <>
          <ActivityDialogExport
            isOpen={isOpenExport}
            setIsOpen={setIsOpenExport}
            dateActivity={dateActivity}
            isLoading={isLoadingSubmit}
            employeeId={employeeId}
          />

          <AppConfirmationDelete
            isOpen={isOpenConfirmation}
            message={messageConfirmationDelete}
            onClose={onCloseDeleteDialog}
            onConfirm={onConfirmDeleteActivity}
          />

          <AppConfirmationSuccessful
            isOpen={successfulDialog.isOpen}
            message={successfulDialog.message}
            onClose={onCloseConfirmDialog}
          />

          <Dialog open={isOpenAddActivity} sx={{ borderRadius: '5px' }}>
            <AppDialogTitle
              title="Add activity"
              handleClose={onCloseAddActivity}
            />
            <ActivityForm
              activity={selectedActivity}
              onClose={onCloseAddActivity}
              idEmployee={employeeId}
              workingHours={userWorkingHours}
              onSubmit={onSubmitAddActivity}
              isLoading={isLoadingSubmit}
            />
          </Dialog>

          <Dialog open={isOpenEditActivity} sx={{ borderRadius: '5px' }}>
            <AppDialogTitle
              title="Edit activity"
              handleClose={onCloseEditDialogActivity}
            />
            <ActivityForm
              onClose={onCloseEditDialogActivity}
              idEmployee={employeeId}
              workingHours={userWorkingHours}
              onSubmit={onSubmitEditActivity}
              activity={selectedActivity}
              isLoading={isLoadingSubmit}
            />
          </Dialog>
          {viewMode === ViewMode.OLD_VIEW ? (
            <>
              {isDateActivityInCurrentMonth && (
                <Box sx={styles.checkboxContainer}>
                  <Typography>Show entire month</Typography>
                  <Checkbox
                    onChange={handleChangeOldViewShowDates}
                    checked={showFullMonth}
                    sx={{ padding: 1 }}
                  />
                </Box>
              )}

              <AppActivityTableViews
                startDate={startDate}
                endDate={endDate}
                location={userDataLocation}
                isEmployeeSection
                activities={activities}
                onEditActivity={onEditActivity}
                onDeleteActivity={onDeleteActivity}
                onAddActivity={onAddActivity}
                employeeWorkHours={userWorkingHours}
              />
            </>
          ) : (
            <AppActivityViews
              activities={activities}
              dateActivity={dateActivity}
              viewMode={viewMode}
              location={userDataLocation}
              isLoading={isLoadingActivities}
              onDayActivities={onDayActivities}
              onDeleteActivity={onDeleteActivity}
              onEditActivity={onEditActivity}
              onWeekActivities={onWeekActivities}
              employeeWorkHours={userWorkingHours}
            />
          )}
        </>
      )}
    </AppMain>
  );
}
